/**
 * A11y-friendly accordion using <details> and Web Animations API (WAAPI).
 * Converts a native toggle into a semi-"smoothly" animated expand/collapse.
 *
 * Copied and modified:
 * https://css-tricks.com/how-to-animate-the-details-element-using-waapi/
 */

/**
 * Usage:
 *  <details
 *   class="sas-accordion [YOUR_COMPONENT_BASECLASS]"
 * >
 *  - transition time using js is taken from CSS Var: --transition-time
 *
 * You can use your own custom CSS2/CSS3 animations using
 * <details
 *   class="sas-accordion [YOUR_COMPONENT_BASECLASS]"
 *   data-accordion-baseclass="[YOUR_COMPONENT_BASECLASS]"
 *   data-no-js-transition="1"
 * >
 *  - data-no-js-transition="1": prevents default but also adds CSS classes
 *    so you can style how it animates open:
 *    add transition on classes:
 *   "[YOUR_COMPONENT_BASECLASS|sas-accordion]__content--init-open" and
 *   "[YOUR_COMPONENT_BASECLASS|sas-accordion]__content--init-close"
 *
 */

const ANIMATION_DURATION_MS = getAnimationTimeMs(getCSSVar("--transition-time"));
const ANIMATION_FUNCTION = "ease-in-out";

function getAnimationTimeMs(cssVar: string): number {
  const v = cssVar.trim();

  // Match for example "0.2s", "200ms" (whitespace tolerant)
  const secondsMatch = new RegExp(/^([\d.]+)\s*s$/i).exec(v);
  if (secondsMatch) {
    const seconds = Number(secondsMatch[1]);
    if (!Number.isNaN(seconds)) {
      return Math.round(seconds * 1000);
    }
  }
  const msMatch = new RegExp(/^([\d.]+)\s*ms$/i).exec(v);
  if (msMatch) {
    const ms = Number(msMatch[1]);
    if (!Number.isNaN(ms)) {
      return Math.round(ms);
    }
  }
  console.warn("Could not parse animation time:", cssVar);
  return -1;
}

function getCSSVar(name: string, el: Element = document.documentElement): string {
  const value = getComputedStyle(el).getPropertyValue(name);
  return value.trim();
}

export default class Accordion {
  private readonly details: HTMLDetailsElement;
  private readonly summary: HTMLElement;
  private readonly content: HTMLElement;
  private animation: Animation | null;
  private isClosing: boolean;
  private isExpanding: boolean;
  private readonly noJsTransition: boolean;
  private ownAnimationTimer: any;
  private readonly baseClass: string;

  constructor(details: HTMLDetailsElement, noTransition = false, baseClass = "sas-accordion") {
    this.details = details;
    this.noJsTransition = noTransition;
    this.baseClass = baseClass;
    this.summary = details.querySelector("summary") as HTMLElement;
    const content = details.querySelector<HTMLElement>(".sas-accordion__content");
    if (!content) {
      throw new Error('Accordion: a child element with class ".sas-accordion__content" is required inside <details>.');
    }
    this.content = content;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    if (this.noJsTransition) {
      this.content.classList.add(`${this.baseClass}__content--no-js-transition`);
    }
    if (this.details.id && window.location.hash === `#${this.details.id}`) {
      this.details.open = true;
    }
    if (this.details.open) {
      this.setTransitionEndAnimationClasses(true);
    }
    this.summary.addEventListener("click", (e: MouseEvent) => this.onClick(e));
  }

  private onClick(e: MouseEvent): void {
    e.preventDefault();
    if (this.isClosing || !this.details.open) {
      this.open();
      this.pushSectionState();
    } else if (this.isExpanding || this.details.open) {
      this.shrink();
      this.removeSectionState();
    }
  }

  private pushSectionState(): void {
    const id = this.details.id;
    if (!id) return;
    history.pushState(null, "", `#${id}`);
  }

  private removeSectionState(): void {
    const id = this.details.id;
    if (!id) return;
    if (window.location.hash === `#${id}`) {
      history.pushState(null, "", window.location.pathname + window.location.search);
    }
  }

  private shrink(): void {
    if (this.noJsTransition) {
      this.content.classList.add(`${this.baseClass}__content--init-close`);
      return this.onAnimationFinish(false);
    }
    this.isClosing = true;
    const startHeight = `${this.details.offsetHeight}px`;
    const compStyleEl = getComputedStyle(this.details);
    const endHeight = `${this.summary.offsetHeight + Number.parseFloat(compStyleEl.paddingTop) + Number.parseFloat(compStyleEl.paddingBottom)}px`;

    if (this.animation) {
      this.animation.cancel();
    }

    // Start a WAAPI animation
    this.animation = this.content.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: ANIMATION_DURATION_MS,
        easing: ANIMATION_FUNCTION,
      }
    );
    this.animation.onfinish = () => this.onAnimationFinish(false);
    this.animation.oncancel = () => {
      this.isClosing = false;
    };
  }

  private open(): void {
    if (this.noJsTransition) {
      this.content.classList.add(`${this.baseClass}__content--init-open`);
      return this.expand();
    }
    this.content.style.height = `0`;
    this.details.open = true;

    // Wait for the next frame to call the expand function
    globalThis.requestAnimationFrame(() => this.expand());
  }

  private expand(): void {
    if (this.noJsTransition) {
      return this.onAnimationFinish(true);
    }
    this.isExpanding = true;
    const startHeight = `0px`;
    this.content.style.height = ``;
    const endHeight = `${this.content.offsetHeight}px`;
    if (this.animation) {
      this.animation.cancel();
    }

    // Start a WAAPI animation
    this.animation = this.content.animate(
      {
        height: [startHeight, endHeight],
      },
      {
        duration: ANIMATION_DURATION_MS,
        easing: ANIMATION_FUNCTION,
      }
    );
    this.animation.onfinish = () => this.onAnimationFinish(true);
    this.animation.oncancel = () => {
      this.isExpanding = false;
    };
  }

  private setTransitionEndAnimationClasses(open: boolean) {
    this.content.classList.remove(`${this.baseClass}__content--init-open`, `${this.baseClass}__content--init-close`);
    if (open) {
      this.content.classList.add(`${this.baseClass}__content--opened`);
    } else {
      this.content.classList.remove(`${this.baseClass}__content--opened`);
    }
  }

  private onAnimationFinish(open: boolean): void {
    if (!this.noJsTransition || !this.details.open) {
      this.details.open = open;
    }
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.details.style.height = "";
    this.details.style.overflow = "";

    if (!this.noJsTransition) {
      return;
    }

    clearTimeout(this.ownAnimationTimer);
    this.ownAnimationTimer = setTimeout(() => {
      this.details.open = open;
      this.setTransitionEndAnimationClasses(open);
    }, ANIMATION_DURATION_MS);
  }
}

interface DetailsExtended extends HTMLDetailsElement {
  extendedAccordion?: Accordion;
}

document.querySelectorAll<HTMLDetailsElement>("details.sas-accordion").forEach((el) => {
  // Guard: only initialize if element actually is a <details>
  if (el instanceof HTMLDetailsElement) {
    (el as DetailsExtended).extendedAccordion = new Accordion(el, !!el.dataset.noJsTransition, el.dataset.accordionBaseclass);
  }
});
